import { useState } from 'react'
import { Button, Callout, FormSection, Icon, toastError, toastSuccess } from 'admination-react'
import { ApiError } from '@/core/api/http'
import { currentVariant } from '@/core/data/session'
import de from '@/core/i18n/de.json'
import { formatDatum } from '@/core/lib/dates'
import { useApp } from '@/core/state/appStore'
import { rueckzugGrundText, type ZurueckgezogenCoaching } from '@/core/types'
import { descriptionFor } from '@/variants'
import { AskModal } from '@/core/components/ui/ask-modal'

const satz = (text: string) => (/[.!?…]$/.test(text) ? text : `${text}.`)

function aufhebenFehler(error: unknown, istNichtZurueckgezogen: (error: unknown) => boolean): string {
  if (error instanceof ApiError && error.status === 403) return "Nur ein Owner kann den Rückzug aufheben."
  if (istNichtZurueckgezogen(error)) return "Diese Person ist nicht zurückgezogen."
  return "Der Rückzug konnte nicht aufgehoben werden."
}

export function RueckzugVermerk({ row, isOwner }: { row: ZurueckgezogenCoaching; isOwner: boolean }) {
  const { dispatch, reloadDashboard, members } = useApp()
  const [fragen, setFragen] = useState(false)
  const [busy, setBusy] = useState(false)
  const rueckzug = descriptionFor(currentVariant()).rueckzug
  const { am, von, grundArt, grund } = row.zurueckgezogen

  const wer = members.find((m) => m.authUserId === von)
  const name = [wer?.vorname, wer?.nachname].filter(Boolean).join(' ') || '-'

  async function aufheben() {
    if (!rueckzug) return
    setBusy(true)
    try {
      await rueckzug.aufheben(row.id)
      toastSuccess("Der Rückzug wurde aufgehoben.")
      dispatch({ type: 'CLOSE' })
    } catch (error) {
      toastError(aufhebenFehler(error, rueckzug.istNichtZurueckgezogen))

      if (rueckzug.istNichtZurueckgezogen(error)) dispatch({ type: 'CLOSE' })
    } finally {
      setBusy(false)
      setFragen(false)
      reloadDashboard()
    }
  }

  return (
    <FormSection>
      <Callout tone="warning" title="Zurückgezogen" data-testid="rueckzug-vermerk">
        {[`Zurückgezogen am ${formatDatum(am)} von ${name}.`, grundArt && satz(`Grund: ${rueckzugGrundText(de.vergabeStaff[`profil.rueckzug.gruende.${grundArt}`], grund)}`), "Die Angaben sind schreibgeschützt."]
          .filter(Boolean)
          .join(' ')}
      </Callout>
      {isOwner && rueckzug ? (
        <div>
          <Button data-testid="rueckzug-aufheben" disabled={busy} onClick={() => setFragen(true)}>
            <Icon name="i-arrow-left" />
            Rückzug aufheben</Button>
        </div>
      ) : null}
      <AskModal
        open={fragen}
        icon="i-person"
        iconStroke
        variant="is-info"
        question="Rückzug aufheben?"
        description={`Die Person kommt zurück auf den Schritt „${row.aufgabe ?? '-'}“ und erscheint wieder auf dem Board.`}
        confirmLabel="Rückzug aufheben"
        cancelLabel="Abbrechen"
        busy={busy}
        onConfirm={() => void aufheben()}
        onCancel={() => setFragen(false)}
      />
    </FormSection>
  )
}
